import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/solid';
import { FilePlus2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Accept } from 'react-dropzone/.';

import { useTheme } from '@/app/ThemeContext';
import { BaseTag, Tabs } from '@/components/chariot-ui';
import { SharedFilePicker } from '@/components/documents/SharedFilePicker';
import { Dropzone, Files } from '@/components/Dropzone';
import { Input } from '@/components/form/Input';
import { getFormValues, Inputs, Values } from '@/components/form/Inputs';
import { Container } from '@/components/legacy-ui';
import { Link } from '@/components/Link';
import { Modal } from '@/components/Modal';
import { Tooltip } from '@/components/Tooltip';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useModifyAccount } from '@/hooks';
import { AnalyticsEvent, useAnalytics } from '@/hooks/useAnalytics';
import { useUploadFile } from '@/hooks/useFiles';
import { useGraphQuery } from '@/hooks/useGraphQuery';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSettings } from '@/hooks/useIntegrationSettings';
import { useJiraTransitions } from '@/hooks/useJiraTransitions';
import {
  useLinearProjects,
  useLinearTeams,
  useLinearWorkflowStates,
} from '@/hooks/useLinearSetup';
import {
  IntegrationValidationError,
  SetupOption,
  ValidateIntegrationErrorResponse,
  ValidationCredentials,
} from '@/hooks/useValidateIntegration';
import { JiraCustomFieldDefaults } from '@/sections/JiraCustomFieldDefaults';
import { JiraPriorityMapping } from '@/sections/JiraPriorityMapping';
import { JiraProjectSearch } from '@/sections/JiraProjectSearch';
import { TransitionsPanel } from '@/sections/TransitionsPanel';
import {
  Account,
  Integration,
  IntegrationInput,
  IntegrationMeta,
  JiraCustomFieldDefault,
  JiraPriorityConfig,
  JiraProject,
  JiraTemplate,
  JiraTransitionConfig,
  JiraTransitionStep,
  LinkAccount,
} from '@/types';
import { cn } from '@/utils/classname';
import { generateUuid } from '@/utils/uuid.util';

// Map vulnerability scanners to their accepted file types.
// Optional sharedFileQuery enables picking from tenant shared files.
interface ScannerConfig {
  accept: Accept;
  sharedFileQuery?: string;
}

const VULNERABILITY_SCANNERS: Record<string, ScannerConfig> = {
  nessus: { accept: { 'application/.nessus': ['.nessus'] } },
  qualys: { accept: { 'text/csv': ['.csv'] } },
  insightvm: { accept: { 'application/xml': ['.xml'] } },
  sharphound: { accept: { 'application/zip': ['.zip'] } },
  cisachvs: { accept: { 'text/csv': ['.csv'] } },
  cisachwas: { accept: { 'text/csv': ['.csv'] } },
  nsacsaasasm: { accept: { 'text/csv': ['.csv'] } },
  palo: {
    accept: { 'application/xml': ['.xml'], 'text/xml': ['.xml'] },
    sharedFileQuery: 'home',
  },
};

type Step = 'setup' | 'configuration' | 'transitions' | 'permissions';

interface SetupModalProps {
  step?: Step;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  selectedIntegration?: Account;
  formData: Values;
  integration?: IntegrationMeta;
  hideAddLater?: boolean;
  parseFormData?: (formData: Values) => Values;
  onSwitchIntegration?: (integration: IntegrationMeta) => void;
}

const configurationTabs = [
  { label: 'Configuration', value: 'configuration' },
  { label: 'Transitions', value: 'transitions' },
  { label: 'Permissions', value: 'permissions' },
];

const SetupModal: React.FC<SetupModalProps> = ({
  step: stepProps = 'setup',
  isOpen,
  onClose,
  onComplete,
  selectedIntegration: connectedIntegration,
  formData: formDataParent,
  integration: integrationMeta,
  parseFormData,
  onSwitchIntegration,
}) => {
  const integration: IntegrationMeta | undefined =
    integrationMeta ||
    (connectedIntegration
      ? AllIntegrations[
          connectedIntegration.member as keyof typeof AllIntegrations
        ]
      : undefined);

  const defaultSetupValues =
    integration?.inputs && formDataParent
      ? { ...getFormValues(integration.inputs), ...formDataParent }
      : undefined;

  const defaultConfigValuesWithoutId = integration?.configuration?.inputs
    ? {
        ...getFormValues(integration?.configuration?.inputs),
        ...formDataParent,
      }
    : undefined;

  const defaultConfigValues = defaultConfigValuesWithoutId
    ? {
        ...defaultConfigValuesWithoutId,
        id: defaultConfigValuesWithoutId.id || generateUuid(),
      }
    : undefined;

  const isVulnerabilityScanner =
    integration?.id && integration.id in VULNERABILITY_SCANNERS;
  const scannerConfig = isVulnerabilityScanner
    ? VULNERABILITY_SCANNERS[
        integration.id as keyof typeof VULNERABILITY_SCANNERS
      ]
    : null;
  const key = connectedIntegration?.key;

  // Mutations
  const { mutateAsync: link, status: linkStatus } = useModifyAccount('link');
  const { mutateAsync: unLink, status: unLinkStatus } =
    useModifyAccount('unlink');
  const { mutateAsync: updateAccount, status: updateAccountStatus } =
    useModifyAccount('update');
  const { mutateAsync: uploadFile } = useUploadFile();
  const integrationSettings = useIntegrationSettings(
    integration?.overrideMemberName || integration?.id,
    connectedIntegration?.value
  );
  const {
    projects,
    onValidateIntegration,
    templatesError,
    setTemplatesError,
    getIntegrationProjects,
    searchProjects,
    setProjects,
  } = integrationSettings;
  const isValidating = integrationSettings.status?.isValidating ?? false;
  const [validationErrors, setValidationErrors] = useState<
    IntegrationValidationError[]
  >([]);
  const { currentTheme } = useTheme();
  const { track } = useAnalytics();

  // Check if we need to fetch assets for dynamic options (e.g., Azure assets for Azure DevOps)
  const assetClassForOptions = useMemo(() => {
    return integration?.inputs?.find(input => input.optionsFromAccounts)
      ?.optionsFromAccounts;
  }, [integration]);

  // Fetch assets with the desired class for dropdown options
  const { data: assetsData, isLoading: isLoadingAssets } = useGraphQuery({
    query: {
      node: {
        labels: ['Asset'],
        filters: assetClassForOptions
          ? [
              { field: 'class', operator: '=', value: assetClassForOptions },
              { field: 'source', operator: '=', value: 'account' },
            ]
          : [],
      },
      limit: 100,
    },
    enabled: !!assetClassForOptions,
  });

  // Process inputs to inject dynamic options from assets
  const processedInputs = useMemo(() => {
    if (!integration?.inputs) return [];

    return integration.inputs.map(input => {
      if (input.optionsFromAccounts) {
        // Still loading assets
        if (isLoadingAssets) {
          return {
            ...input,
            options: [],
            placeholder: 'Loading integrations...',
            disabled: true,
          };
        }

        const assets = assetsData?.assets || [];

        // If no assets found, return input with empty options and a helpful message
        if (assets.length === 0) {
          return {
            ...input,
            options: [],
            placeholder: `No ${input.optionsFromAccounts} integrations found`,
            disabled: true,
          };
        }

        // Map assets to select options using asset key
        const options = assets.map(asset => ({
          label: asset.name || asset.key,
          value: asset.key,
        }));

        // Auto-select if only one option
        const defaultValue = assets.length === 1 ? assets[0].key : input.value;

        return {
          ...input,
          options,
          value: defaultValue,
        };
      }
      return input;
    });
  }, [integration, assetsData, isLoadingAssets]);

  // Local States
  const [step, setStep] = useState<
    'setup' | 'configuration' | 'transitions' | 'permissions'
  >(stepProps);
  const [setupData, setSetupData] = useState<Values>(defaultSetupValues || {});
  const [prevIntegrationId, setPrevIntegrationId] = useState(integration?.id);

  // Reset setupData when integration changes (e.g., switching from azuredevops to azuredevops-pat)
  // to prevent stale fields (like parentKey) from leaking into the new form submission.
  // Uses synchronous state reset pattern (not useEffect) to ensure setupData is clean
  // before the Inputs component renders with the new integration's fields.
  if (integration?.id !== prevIntegrationId) {
    setPrevIntegrationId(integration?.id);
    const newDefaults = integration?.inputs
      ? getFormValues(integration.inputs)
      : {};
    setSetupData(newDefaults);
  }

  // Update setupData when processedInputs has auto-selected values (e.g., single Azure account)
  // Using queueMicrotask to avoid synchronous setState in effect body
  useEffect(() => {
    queueMicrotask(() => {
      setSetupData(prev => {
        const autoSelectedValues = processedInputs.reduce((acc, input) => {
          // Auto-select if input has optionsFromAccounts, has a value, and setupData doesn't have a value yet
          const currentValue = prev[input.name];
          const shouldAutoSelect =
            input.optionsFromAccounts &&
            input.value &&
            (currentValue === '' || currentValue === undefined);
          if (shouldAutoSelect) {
            return { ...acc, [input.name]: input.value };
          }
          return acc;
        }, {} as Values);

        if (Object.keys(autoSelectedValues).length > 0) {
          return { ...prev, ...autoSelectedValues };
        }
        return prev;
      });
    });
  }, [processedInputs]);
  const [configData, setConfigData] = useState<Values>(
    defaultConfigValues || {}
  );

  // Linear has no Jira-style `projectKey`; its project is chosen via `projectId`
  // and the project list comes from `useLinearProjects`. Mounted here (in
  // addition to ConfigurationSection) so `prepareFormData` can resolve the
  // selected project's name. `enabled` gating inside the hook keys on apiKey +
  // accountKey, so it's a no-op for non-Linear integrations. The query is cached
  // by key, so this shares the ConfigurationSection fetch rather than duplicating
  // it.
  const isLinearIntegration = integration?.id === Integration.linear;
  const linearCredentialBlob = {
    accountKey: isLinearIntegration ? key : undefined,
    config:
      isLinearIntegration && setupData?.apiKey
        ? { apiKey: String(setupData.apiKey) }
        : undefined,
  };
  const { data: linearProjects } = useLinearProjects({
    ...linearCredentialBlob,
    teamId: configData?.teamId ? String(configData.teamId) : undefined,
  });
  // Linear teams are mounted here (in addition to ConfigurationSection) so
  // `prepareFormData` can resolve the selected team's human-readable key/name
  // (e.g. "ENG" / "Engineering") from `configData.teamId`. The Linear ticket-
  // routing dialog renders teamKey/teamName/projectName, not the stored UUIDs.
  // The query is cached by key, so this shares the ConfigurationSection fetch
  // rather than duplicating it; `enabled` gating inside the hook keys on
  // apiKey + accountKey, so it's a no-op for non-Linear integrations.
  const { data: linearTeams } = useLinearTeams(linearCredentialBlob);

  const [transitionsData, setTransitionsData] = useState<JiraTransitionConfig>(
    () => {
      // Initialize from existing template when editing
      const templateId = defaultConfigValues?.id;
      const existingTemplates = connectedIntegration?.settings?.templates ?? [];
      const existingTemplate = existingTemplates.find(
        t => t.id === templateId
      ) as JiraTemplate | undefined;

      return (
        existingTemplate?.transitions ?? {
          openToClose: [],
          closeToOpen: [],
        }
      );
    }
  );
  const [customFieldDefaults, setCustomFieldDefaults] = useState<
    JiraCustomFieldDefault[]
  >(() => {
    // Initialize from existing template when editing — same pattern as transitions.
    const templateId = defaultConfigValues?.id;
    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const existingTemplate = existingTemplates.find(
      t => t.id === templateId
    ) as JiraTemplate | undefined;
    return existingTemplate?.customFieldDefaults ?? [];
  });
  const [priorityConfig, setPriorityConfig] = useState<
    JiraPriorityConfig | undefined
  >(() => {
    const templateId = defaultConfigValues?.id;
    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const existingTemplate = existingTemplates.find(
      t => t.id === templateId
    ) as JiraTemplate | undefined;
    return existingTemplate?.priority;
  });
  const [retestTransitions, setRetestTransitions] = useState<
    JiraTransitionStep[]
  >(() => {
    const templateId = defaultConfigValues?.id;
    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const existingTemplate = existingTemplates.find(
      t => t.id === templateId
    ) as JiraTemplate | undefined;
    return existingTemplate?.retestTransitions ?? [];
  });
  const [additionalResolvedStatuses, setAdditionalResolvedStatuses] = useState<
    JiraTransitionStep[]
  >(() => {
    const templateId = defaultConfigValues?.id;
    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const existingTemplate = existingTemplates.find(
      t => t.id === templateId
    ) as JiraTemplate | undefined;
    return existingTemplate?.additionalResolvedStatuses ?? [];
  });

  const [availableOrgs, setAvailableOrgs] = useState<SetupOption[]>([]);
  const [selectedOrgIds, setSelectedOrgIds] = useState<Set<string>>(new Set());
  const [orgsValidated, setOrgsValidated] = useState(false);

  if (integration?.id !== prevIntegrationId && availableOrgs.length > 0) {
    setAvailableOrgs([]);
    setSelectedOrgIds(new Set());
    setOrgsValidated(false);
  }

  // Memoized values
  const isEditingMode = useMemo(() => {
    return !!integration?.configuration && !!key;
  }, [integration?.configuration, key]);

  const {
    formErrors,
    setFormErrors,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
    validateForm,
  } = useFormValidation();

  // Wrapper for searchProjects that handles both new and existing integrations
  const handleSearchProjects = useCallback(
    async (searchTerm: string) => {
      // For existing integrations (has key), searchProjects uses the account key
      // For new integrations (no key), pass the credentials from setupData
      const config = key ? undefined : convertObjectValuesToStrings(setupData);
      return searchProjects(searchTerm, config);
    },
    [key, setupData, searchProjects]
  );

  useEffect(() => {
    if (step === 'configuration') {
      if (key) {
        // Existing integration - load projects using account key
        getIntegrationProjects();
      } else if (Object.keys(setupData).length > 0) {
        // New integration - load projects using credentials
        handleSearchProjects('').then(initialProjects => {
          setProjects(initialProjects);
        });
      }
    }
  }, [
    key,
    step,
    getIntegrationProjects,
    handleSearchProjects,
    setupData,
    setProjects,
  ]);

  const handleSetupInputsChange = useCallback(
    (newValues: Values) => {
      setSetupData(newValues);
      setValidationErrors([]); // Clear validation errors on input change

      if (availableOrgs.length > 0) {
        setAvailableOrgs([]);
        setSelectedOrgIds(new Set());
        setOrgsValidated(false);
      }

      // If user has attempted to submit, validate on change
      if (hasAttemptedSubmit) {
        setFormErrors(prevErrors => {
          const newErrors: Record<string, boolean> = { ...prevErrors };
          Object.keys(newValues).forEach(key => {
            if (newValues[key]) {
              delete newErrors[key];
            }
          });
          return newErrors;
        });
      }
    },
    [hasAttemptedSubmit, setFormErrors, availableOrgs.length]
  );

  const handleConfigDataChange = (newValues: Values) => {
    setFormErrors({});
    setTemplatesError({});
    setValidationErrors([]); // Clear validation errors on input change
    setConfigData(currentValues => {
      // Preserve projectKey if newValues has empty/default value (from Inputs component)
      // Only update projectKey if newValues has a non-empty value (from JiraProjectSearch)
      const projectKey = newValues.projectKey || currentValues.projectKey;
      const merged = { ...currentValues, ...newValues, projectKey };
      const projectKeyChanged = currentValues.projectKey !== projectKey;

      if (projectKeyChanged) {
        return { ...merged, issueType: '' };
      }
      return merged;
    });
  };

  // const handleSetupLater = async () => {
  //   const hasInputs = Object.keys(integration || {}).includes('inputs');

  //   await link({
  //     username: integration?.id || '',
  //     value: hasInputs ? 'setup' : 'waitlisted',
  //     config: {},
  //   });

  //   onComplete();
  //   onClose();
  // };

  const handleFileDrop = async (files: Files<'arrayBuffer'>) => {
    if (!integration?.id) return;

    try {
      const { file, content } = files[0];
      const fileUploadName = `imports/${integration.id}/${file.name}`;

      await uploadFile({
        name: fileUploadName,
        content,
      });

      await link({
        value: fileUploadName,
        config: {},
        username: `${integration.id}-import`,
      });

      track(AnalyticsEvent.INTEGRATION_CONNECTED, {
        integration: integration.id,
      });
      onComplete();
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  const handleSharedFileSelect = async (filePath: string) => {
    if (!integration?.id) return;

    try {
      await link({
        value: filePath,
        config: {},
        username: `${integration.id}-import`,
      });

      track(AnalyticsEvent.INTEGRATION_CONNECTED, {
        integration: integration.id,
      });
      onComplete();
      onClose();
    } catch (error) {
      console.error('Error importing shared file:', error);
    }
  };

  const validateIntegrationConfig = async (preparedFormData: LinkAccount) => {
    setValidationErrors([]); // Clear previous errors

    if (!integration?.validate && !integration?.configuration) return true;

    if (orgsValidated && availableOrgs.length > 0) {
      if (selectedOrgIds.size === 0) {
        setValidationErrors([
          {
            code: 'org_selection',
            title: 'No Organizations Selected',
            message:
              'Please select at least one organization to import data from.',
          },
        ]);
        return false;
      }
      return true;
    }

    const {
      isValid,
      templatesError: templatesErrorResponse,
      validationErrors: errors,
      organizations,
    } = await onValidateIntegration(
      {
        account: preparedFormData.key ?? '',
        config: preparedFormData.config,
        settings: preparedFormData.settings,
        templateId:
          step === 'configuration' ? String(configData.id) : undefined,
      },
      {
        suppressErrorToast: true, // Always suppress toast, show inline instead
      }
    );

    const templateError = configData.id
      ? templatesErrorResponse?.[configData.id]
      : undefined;

    if (step === 'configuration' && templateError) {
      setStep('permissions');
      return false;
    }

    if (templateError) return false;

    if (!isValid) {
      // Store validation errors for inline display
      if (errors && errors.length > 0) {
        setValidationErrors(errors);
      }
      track(AnalyticsEvent.INTEGRATION_VALIDATE_FAILURE, {
        integration: integration?.id || '',
      });
      return false;
    }

    track(AnalyticsEvent.INTEGRATION_VALIDATE_SUCCESS, {
      integration: integration?.id || '',
    });

    if (organizations && organizations.length > 0 && !orgsValidated) {
      setAvailableOrgs(organizations);
      setSelectedOrgIds(new Set(organizations.map(o => o.id)));
      setOrgsValidated(true);
      return false;
    }

    if (step === 'setup' && isValid && integration.configuration) {
      setStep('configuration');
      return false;
    }

    return true;
  };

  const prepareFormData = (): LinkAccount => {
    const setupDataRequestObj = parseFormData
      ? parseFormData(setupData)
      : setupData;
    const { displayName, ...restConfig } = configData;

    // Coerce a derived optional field that may be JS `undefined` (no matching
    // project) or a stale `"undefined"`/`"null"` string into ''. Without this
    // the value gets `String(undefined)`-ed to the literal "undefined" when the
    // template is string-coerced in buildSettings.
    //
    // Jira matches projects by `projectKey`; Linear has no `projectKey` and
    // instead selects a project via `projectId` (resolved against the Linear
    // projects list). Derive the name from whichever identifier applies.
    const rawProjectName = isLinearIntegration
      ? (linearProjects ?? []).find(
          project => project.id === restConfig.projectId
        )?.name
      : projects.find(({ key }) => key === restConfig.projectKey)?.name;
    const projectName = emptyIfUndefinedLike(rawProjectName);

    // Linear routes by team, not by a Jira-style project key, and the ticket-
    // routing dialog displays the team's human-readable key/name (not the stored
    // `teamId` UUID). Resolve both from the selected `teamId` against the Linear
    // teams list so the dialog has something to render. `LinearTeam` is
    // `{ id, key, name }`. Coerced through `emptyIfUndefinedLike` so an
    // unresolved team never serializes the literal "undefined".
    const selectedLinearTeam = isLinearIntegration
      ? (linearTeams ?? []).find(team => team.id === restConfig.teamId)
      : undefined;
    const linearTeamFields = isLinearIntegration
      ? {
          teamKey: emptyIfUndefinedLike(selectedLinearTeam?.key),
          teamName: emptyIfUndefinedLike(selectedLinearTeam?.name),
        }
      : {};

    const existingTemplates = connectedIntegration?.settings?.templates ?? [];
    const currentTemplate = {
      // Strip any undefined / null / "undefined" / "null" carried in from the
      // form (e.g. a previously-saved projectKey === "undefined") so they can
      // never be serialized back into the saved template.
      ...sanitizeTemplate(restConfig),
      projectName,
      ...linearTeamFields,
      ...(integration?.transitions && transitionsData.openToClose.length > 0
        ? { transitions: transitionsData }
        : {}),
      ...(customFieldDefaults.length > 0 ? { customFieldDefaults } : {}),
      ...(priorityConfig?.enabled ? { priority: priorityConfig } : {}),
      ...(integration?.id === 'jira' && retestTransitions.length > 0
        ? { retestTransitions }
        : {}),
      ...(integration?.id === 'jira' && additionalResolvedStatuses.length > 0
        ? { additionalResolvedStatuses }
        : {}),
    };

    const hasMatchingTemplate = existingTemplates.some(
      template => template.id === restConfig.id
    );
    const newTemplates = isEditingMode
      ? hasMatchingTemplate
        ? existingTemplates.map(template =>
            template.id === restConfig.id ? currentTemplate : template
          )
        : [...existingTemplates, currentTemplate]
      : [...existingTemplates, currentTemplate];

    const config = convertObjectValuesToStrings(setupDataRequestObj);

    if (availableOrgs.length > 0 && selectedOrgIds.size > 0) {
      config.selected_orgs = JSON.stringify([...selectedOrgIds]);
    }

    // Extract settings-only fields from config so they do not get persisted to
    // SSM secrets. Notification severity filtering is driven by
    // settings.severityThreshold, not secret.severities.
    const apiUsageLimit = config.api_usage_limit;
    delete config.api_usage_limit;
    delete config.severities;
    delete config.severityThreshold;

    const buildSettings = (): LinkAccount['settings'] | undefined => {
      if (
        step === 'configuration' ||
        step === 'transitions' ||
        step === 'permissions'
      ) {
        return {
          displayName: String(displayName),
          templates: (newTemplates as JiraTemplate[]).map(value => {
            // Extract nested non-string properties before string-coercion so
            // they survive intact through buildSettings.
            const {
              transitions,
              customFieldDefaults: nestedCustomFieldDefaults,
              priority,
              retestTransitions: nestedRetestTransitions,
              additionalResolvedStatuses: nestedAdditionalResolvedStatuses,
              ...rest
            } = value;
            const converted = convertObjectValuesToStrings(rest);
            return {
              ...converted,
              ...(transitions ? { transitions } : {}),
              ...(nestedCustomFieldDefaults &&
              nestedCustomFieldDefaults.length > 0
                ? { customFieldDefaults: nestedCustomFieldDefaults }
                : {}),
              ...(priority?.enabled ? { priority } : {}),
              ...(nestedRetestTransitions && nestedRetestTransitions.length > 0
                ? { retestTransitions: nestedRetestTransitions }
                : {}),
              ...(nestedAdditionalResolvedStatuses &&
              nestedAdditionalResolvedStatuses.length > 0
                ? {
                    additionalResolvedStatuses:
                      nestedAdditionalResolvedStatuses,
                  }
                : {}),
            } as ValidationCredentials & {
              transitions?: JiraTransitionConfig;
              customFieldDefaults?: JiraCustomFieldDefault[];
              priority?: JiraPriorityConfig;
              retestTransitions?: JiraTransitionStep[];
              additionalResolvedStatuses?: JiraTransitionStep[];
            };
          }) as (ValidationCredentials & {
            transitions?: JiraTransitionConfig;
            customFieldDefaults?: JiraCustomFieldDefault[];
            priority?: JiraPriorityConfig;
            retestTransitions?: JiraTransitionStep[];
            additionalResolvedStatuses?: JiraTransitionStep[];
          })[],
        };
      }

      if (setupDataRequestObj.severityThreshold) {
        return {
          severityThreshold: String(setupDataRequestObj.severityThreshold),
        };
      }

      if (apiUsageLimit && Number(apiUsageLimit) > 0) {
        const limit = Number(apiUsageLimit);
        const headroom = Math.ceil(limit * 0.1);
        return { api_usage: { limit, headroom } };
      }

      return undefined;
    };

    // Some integrations (e.g. Linear) have no `value` setup field; stringifying
    // a missing value would persist the literal "undefined" as the account
    // value. Default to '' instead of serializing undefined/null.
    const rawValue = emptyIfUndefinedLike(setupDataRequestObj.value);
    const result: LinkAccount = {
      username: integration?.overrideMemberName || integration?.id || '',
      value:
        integration?.id === 'jira' ? rawValue.replace(/\/+$/, '') : rawValue,
      config,
      key,
      settings: buildSettings(),
    };

    return result;
  };

  const handleLinkUnlinkIntegration = async (integrationData: LinkAccount) => {
    if (
      connectedIntegration &&
      !integration?.multiple &&
      !integration?.configuration
    ) {
      await unLink({
        username: connectedIntegration.member,
        value: connectedIntegration.value,
        config: connectedIntegration.config,
      });
    }
    if (isEditingMode) {
      await updateAccount(integrationData);
    } else {
      await link(integrationData);
    }
    track(AnalyticsEvent.INTEGRATION_CONNECTED, {
      integration: integrationData.username,
    });
    onComplete();
    onClose();
  };

  const handleLinkIntegration = async () => {
    setHasAttemptedSubmit(true);
    setValidationErrors([]); // Clear previous errors
    const inputs =
      step === 'setup'
        ? integration?.inputs
        : integration?.configuration?.inputs;

    // Client side validation
    if (!validateForm(inputs, step === 'setup' ? setupData : configData)) {
      setValidationErrors([
        {
          code: 'form_validation',
          title: 'Missing Required Fields',
          message: 'Please fill in all required fields before continuing.',
        },
      ]);
      return;
    }

    try {
      let integrationData = prepareFormData();
      if (!(await validateIntegrationConfig(integrationData))) {
        return;
      }

      await handleLinkUnlinkIntegration(integrationData as LinkAccount);
    } catch (error) {
      setValidationErrors([
        {
          code: 'unexpected_error',
          title: 'Connection Failed',
          message:
            error instanceof Error
              ? error.message
              : 'An unexpected error occurred while connecting the integration.',
        },
      ]);
    }
  };

  // Map inputs to include error state (use processedInputs for dynamic options)
  // Memoized to prevent unnecessary re-renders of Inputs component
  const inputsWithErrors = useMemo(
    () =>
      processedInputs?.map(input => ({
        ...input,
        error: formErrors[input.name] ? ' ' : undefined, // Using space to trigger error state without showing message
      })) || [],
    [processedInputs, formErrors]
  );

  if (!integration) return null;

  const getModalFooterConfig = () => {
    const isLoading =
      isValidating ||
      [unLinkStatus, linkStatus, updateAccountStatus].includes('pending');

    // Block submit until every required SELECT for the active step holds a real
    // selection. This is what prevents a half-built routing config (the source
    // of persisted "undefined" team/project values) from being saved.
    const stepInputs =
      step === 'setup'
        ? integration?.inputs
        : integration?.configuration?.inputs;
    const stepData = step === 'setup' ? setupData : configData;
    const hasMissingRequiredSelect =
      getMissingRequiredSelects(stepInputs, stepData).length > 0;

    const isTryAgain =
      step === 'permissions' && Object.keys(templatesError ?? {}).length > 0;

    return {
      hideCancelButton: false,
      text: isTryAgain
        ? 'Try Again'
        : isLoading
          ? 'Validating...'
          : isEditingMode
            ? 'Save'
            : 'Connect',
      onClick: isTryAgain
        ? () => setStep('configuration')
        : handleLinkIntegration,
      // "Try Again" only re-opens the config step, so don't gate it on selects.
      disabled: isLoading || (!isTryAgain && hasMissingRequiredSelect),
      className: 'px-6',
    };
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex w-full flex-row items-center gap-3">
          <img
            src={`/icons/${currentTheme}/${integration.logo}`}
            alt={integration.name}
            className="size-6"
          />
          <p>
            {integration.name} {step === 'setup' ? 'Setup' : 'Configuration'}
          </p>
        </div>
      }
      hideFooter={integration.hideFooter}
      footer={getModalFooterConfig()}
    >
      {/* Inline validation error display */}
      {validationErrors.length > 0 && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 size-5 shrink-0 text-red-500" />
            <div className="flex-1">
              {validationErrors.length === 1 ? (
                <>
                  <p className="text-sm font-medium text-red-500">
                    {validationErrors[0].title}
                  </p>
                  <p className="mt-1 text-sm text-red-400">
                    {validationErrors[0].message}
                  </p>
                  {validationErrors[0].hint && (
                    <p className="text-chariot-text-secondary mt-2 text-sm">
                      💡 {validationErrors[0].hint}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-red-500">
                    Validation Failed
                  </p>
                  <ul className="mt-2 space-y-2 text-sm text-red-400">
                    {validationErrors.map((error, index) => (
                      <li key={index}>
                        <span className="font-medium">{error.title}:</span>{' '}
                        {error.message}
                        {error.hint && (
                          <p className="text-chariot-text-secondary mt-1 text-xs">
                            💡 {error.hint}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 'setup' && (
        <>
          <SetupSection
            integration={integration}
            inputsWithErrors={inputsWithErrors}
            setupData={setupData}
            onComplete={onComplete}
            defaultSetupValues={defaultSetupValues}
            handleSetupInputsChange={handleSetupInputsChange}
            handleFileDrop={handleFileDrop}
            handleSharedFileSelect={handleSharedFileSelect}
            isVulnerabilityScanner={isVulnerabilityScanner}
            scannerConfig={scannerConfig}
            onClose={onClose}
            onSwitchToFallback={
              integration?.fallbackIntegration && onSwitchIntegration
                ? () => {
                    const fallback =
                      AllIntegrations[integration.fallbackIntegration!];
                    if (fallback) {
                      onSwitchIntegration(fallback);
                    }
                  }
                : undefined
            }
          />
          {availableOrgs.length > 0 && (
            <OrgSelector
              organizations={availableOrgs}
              selectedIds={selectedOrgIds}
              onChange={setSelectedOrgIds}
            />
          )}
        </>
      )}

      {(step === 'configuration' ||
        step === 'transitions' ||
        step === 'permissions') && (
        <ConfigurationSection
          step={step}
          setStep={setStep}
          integration={integration}
          templatesError={templatesError}
          projects={projects}
          formErrors={formErrors}
          configData={configData}
          setupData={setupData}
          defaultConfigValues={defaultConfigValues}
          handleConfigDataChange={handleConfigDataChange}
          searchProjects={handleSearchProjects}
          setProjects={setProjects}
          transitionsData={transitionsData}
          onTransitionsChange={setTransitionsData}
          customFieldDefaults={customFieldDefaults}
          onCustomFieldDefaultsChange={setCustomFieldDefaults}
          priorityConfig={priorityConfig}
          onPriorityConfigChange={setPriorityConfig}
          retestTransitions={retestTransitions}
          onRetestTransitionsChange={setRetestTransitions}
          additionalResolvedStatuses={additionalResolvedStatuses}
          onAdditionalResolvedStatusesChange={setAdditionalResolvedStatuses}
          accountKey={key}
        />
      )}
    </Modal>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- Hook exported alongside components for convenience
export const useFormValidation = () => {
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const validateInputField = (
    input: IntegrationInput,
    value: string | number
  ): boolean => {
    if (input.required && !value) return false;
    if (input.pattern && value) {
      const regex = new RegExp(input.pattern);
      return regex.test(value as string);
    }
    return true;
  };

  const validateForm = (
    inputs: IntegrationMeta['inputs'],
    data: Values
  ): boolean => {
    if (!inputs) return true;

    const newErrors: Record<string, boolean> = {};
    let isValid = true;

    (inputs || []).forEach(input => {
      if (!validateInputField(input, data[input.name])) {
        newErrors[input.name] = true;
        isValid = false;
      }
    });

    setFormErrors(newErrors);
    return isValid;
  };

  return {
    formErrors,
    setFormErrors,
    hasAttemptedSubmit,
    setHasAttemptedSubmit,
    validateForm,
  };
};

interface SetupSectionProps {
  integration: IntegrationMeta;
  onComplete: () => void;
  inputsWithErrors: IntegrationInput[];
  setupData: Values;
  defaultSetupValues?: Values;
  handleSetupInputsChange: (newValues: Values) => void;
  handleFileDrop: (files: Files<'arrayBuffer'>) => void;
  handleSharedFileSelect: (filePath: string) => void;
  isVulnerabilityScanner?: boolean;
  scannerConfig: ScannerConfig | null;
  onClose: () => void;
  onSwitchToFallback?: () => void;
}

export const SetupSection: React.FC<SetupSectionProps> = ({
  integration,
  onComplete,
  inputsWithErrors,
  setupData,
  defaultSetupValues,
  handleSetupInputsChange,
  handleFileDrop,
  handleSharedFileSelect,
  isVulnerabilityScanner,
  scannerConfig,
  onClose,
  onSwitchToFallback,
}) => {
  const [importMode, setImportMode] = useState<'upload' | 'shared'>('upload');
  const fallbackIntegration = integration.fallbackIntegration
    ? AllIntegrations[integration.fallbackIntegration]
    : null;

  return (
    <>
      {integration.help && (
        <div className="bg-chariot-background-secondary rounded-lg px-4 py-2">
          <p className="mb-1 text-sm font-bold">Need help?</p>
          <div className="flex flex-col space-y-2">
            <Link
              styleType="text"
              to={integration.help.href}
              target="_blank"
              rel="noopener noreferrer"
              buttonClass="p-0 hover:underline text-chariot-text-secondary font-base"
            >
              <InformationCircleIcon className="size-5" />
              <span className="underline">{integration.help.label}</span>
            </Link>
          </div>
        </div>
      )}

      {integration.Markup && (
        <div className="mt-4">
          <integration.Markup onComplete={onComplete} onClose={onClose} />
        </div>
      )}

      <div className="mt-4 space-y-4">
        <Inputs
          key={integration.id}
          inputs={inputsWithErrors}
          onChange={handleSetupInputsChange}
          values={setupData}
          defaultValues={defaultSetupValues}
        />

        {isVulnerabilityScanner && scannerConfig && (
          <>
            {inputsWithErrors.length > 0 &&
              (integration.inputs?.length ?? 0) > 0 && (
                <div className="relative mt-8">
                  <div className="relative flex justify-center">
                    <span className="bg-chariot-background-primary px-8 text-center font-semibold">
                      Or
                    </span>
                  </div>
                </div>
              )}

            {scannerConfig.sharedFileQuery && (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={importMode === 'upload' ? 'default' : 'outline'}
                  onClick={() => setImportMode('upload')}
                >
                  Upload File
                </Button>
                <Button
                  size="sm"
                  variant={importMode === 'shared' ? 'default' : 'outline'}
                  onClick={() => setImportMode('shared')}
                >
                  From Shared Files
                </Button>
              </div>
            )}

            {importMode === 'upload' && (
              <Dropzone
                type="arrayBuffer"
                className="border-chariot-stroke bg-chariot-background-primary rounded-lg border-2 border-dashed p-2"
                onFilesDrop={handleFileDrop}
                accept={scannerConfig.accept}
              >
                <div className="flex flex-col items-center p-2">
                  <FilePlus2 className="text-chariot-text-secondary size-8" />
                  <h3 className="m-0 mt-2 p-0 text-center text-lg font-medium">
                    Import {integration.name} Data
                  </h3>
                  <p className="text-chariot-text-primary mb-3 text-center text-sm">
                    Drag & Drop or Choose file to import
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-md rounded-full p-2 py-0.5"
                    onClick={() => {}}
                  >
                    <PlusCircleIcon className="size-5" />
                    Choose Files
                  </Button>
                </div>
              </Dropzone>
            )}

            {importMode === 'shared' && scannerConfig.sharedFileQuery && (
              <SharedFilePicker
                query={scannerConfig.sharedFileQuery}
                acceptedExtensions={Object.values(scannerConfig.accept).flat()}
                onSelect={filePath => handleSharedFileSelect(filePath)}
              />
            )}
          </>
        )}
      </div>

      {fallbackIntegration && onSwitchToFallback && (
        <div className="border-chariot-stroke mt-6 border-t pt-4">
          <p className="text-chariot-text-secondary text-sm">
            {integration.fallbackDescription ??
              'Try an alternative authentication method.'}{' '}
            <button
              type="button"
              onClick={onSwitchToFallback}
              className="text-chariot-brand-primary hover:underline"
            >
              {integration.fallbackLabel ?? 'Use alternative authentication →'}
            </button>
          </p>
        </div>
      )}
    </>
  );
};

interface ConfigurationSectionProps {
  step: 'configuration' | 'transitions' | 'permissions';
  setStep: (step: 'configuration' | 'transitions' | 'permissions') => void;
  integration: IntegrationMeta;
  templatesError: ValidateIntegrationErrorResponse['templates'];
  formErrors: Record<string, boolean>;
  configData: Values;
  setupData: Values;
  projects: JiraProject[];
  defaultConfigValues?: Values;
  handleConfigDataChange: (newValues: Values) => void;
  searchProjects: (searchTerm: string) => Promise<JiraProject[]>;
  setProjects: React.Dispatch<React.SetStateAction<JiraProject[]>>;
  transitionsData?: JiraTransitionConfig;
  onTransitionsChange?: (data: JiraTransitionConfig) => void;
  customFieldDefaults?: JiraCustomFieldDefault[];
  onCustomFieldDefaultsChange?: (data: JiraCustomFieldDefault[]) => void;
  priorityConfig?: JiraPriorityConfig;
  onPriorityConfigChange?: (data: JiraPriorityConfig | undefined) => void;
  retestTransitions?: JiraTransitionStep[];
  onRetestTransitionsChange?: (data: JiraTransitionStep[]) => void;
  additionalResolvedStatuses?: JiraTransitionStep[];
  onAdditionalResolvedStatusesChange?: (data: JiraTransitionStep[]) => void;
  accountKey?: string;
}

export const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  step,
  setStep,
  integration,
  configData,
  setupData,
  templatesError,
  formErrors,
  defaultConfigValues,
  handleConfigDataChange,
  projects,
  searchProjects,
  setProjects: _setProjects,
  transitionsData,
  onTransitionsChange,
  customFieldDefaults,
  onCustomFieldDefaultsChange,
  priorityConfig,
  onPriorityConfigChange,
  retestTransitions,
  onRetestTransitionsChange,
  additionalResolvedStatuses,
  onAdditionalResolvedStatusesChange,
  accountKey,
}) => {
  // Check if this is a ticketing integration with project/issue type selection (Jira, Azure DevOps, etc.)
  const isTicketingIntegration = integration?.configuration?.inputs?.some(
    input => input.name === 'projectKey'
  );

  const isLinearIntegration = integration?.id === Integration.linear;

  // Linear setup hooks fire only for Linear. `enabled` gating inside each hook
  // is keyed on apiKey + accountKey, so they're safe to mount unconditionally
  // for the non-Linear case.
  const linearCredentialBlob = isLinearIntegration
    ? {
        accountKey,
        config: setupData?.apiKey
          ? { apiKey: String(setupData.apiKey) }
          : undefined,
      }
    : { accountKey: undefined, config: undefined };

  const { data: linearTeams } = useLinearTeams(linearCredentialBlob);
  const { data: linearWorkflowStates } = useLinearWorkflowStates({
    ...linearCredentialBlob,
    teamId: configData?.teamId ? String(configData.teamId) : undefined,
  });
  const { data: linearProjects } = useLinearProjects({
    ...linearCredentialBlob,
    teamId: configData?.teamId ? String(configData.teamId) : undefined,
  });

  const { data: jiraTransitionsData } = useJiraTransitions({
    accountKey,
    projectKey: configData.projectKey as string,
    config: accountKey ? undefined : convertObjectValuesToStrings(setupData),
    enabled:
      integration?.id === 'jira' &&
      isTicketingIntegration &&
      !!configData.projectKey,
  });
  const availableStatuses = jiraTransitionsData?.statuses ?? [];

  const getOptions = useCallback(
    (input: IntegrationInput, name: string) => {
      if (name === 'projectKey') {
        return projects.map(project => ({
          label: `${project.name} (${project.key})`,
          value: project.key,
        }));
      }
      if (name === 'issueType') {
        return projects
          .find(project => project.key === configData?.projectKey)
          ?.issueTypes.map(({ name }) => ({ label: name, value: name }));
      }
      // Linear-specific dropdowns.
      if (isLinearIntegration && name === 'teamId') {
        return (linearTeams ?? []).map(team => ({
          label: `${team.name} (${team.key})`,
          value: team.id,
        }));
      }
      if (
        isLinearIntegration &&
        (name === 'openStateId' || name === 'closeStateId')
      ) {
        return (linearWorkflowStates?.states ?? []).map(state => ({
          label: `${state.name} (${state.type})`,
          value: state.id,
        }));
      }
      if (isLinearIntegration && name === 'projectId') {
        const opts = (linearProjects ?? []).map(project => ({
          label: project.name,
          value: project.id,
        }));
        // Allow the user to clear an optional project selection.
        return [{ label: '— No project —', value: '' }, ...opts];
      }
      return input.options;
    },
    [
      projects,
      configData,
      isLinearIntegration,
      linearTeams,
      linearWorkflowStates,
      linearProjects,
    ]
  );

  // When the Linear team changes, reset every team-scoped dependent field so
  // stale state IDs (from a different team) can't sneak into the saved
  // settings blob.
  const handleConfigDataChangeWithLinearReset = (newValues: Values) => {
    if (
      isLinearIntegration &&
      newValues.teamId !== undefined &&
      newValues.teamId !== configData?.teamId
    ) {
      handleConfigDataChange({
        ...newValues,
        openStateId: '',
        closeStateId: '',
        projectId: '',
      });
      return;
    }
    handleConfigDataChange(newValues);
  };

  // Auto-fill suggested open/close state IDs when workflow-states responds
  // with them and the user hasn't picked one yet.
  useEffect(() => {
    if (!isLinearIntegration || !linearWorkflowStates) return;
    const next: Values = {};
    if (!configData?.openStateId && linearWorkflowStates.suggestedOpenStateId) {
      next.openStateId = linearWorkflowStates.suggestedOpenStateId;
    }
    if (
      !configData?.closeStateId &&
      linearWorkflowStates.suggestedCloseStateId
    ) {
      next.closeStateId = linearWorkflowStates.suggestedCloseStateId;
    }
    if (Object.keys(next).length > 0) {
      handleConfigDataChange({ ...configData, ...next });
    }
    // We intentionally depend on the suggestion identity rather than the whole
    // hook result object — the latter is referentially unstable from React
    // Query.
  }, [
    isLinearIntegration,
    linearWorkflowStates?.suggestedOpenStateId,
    linearWorkflowStates?.suggestedCloseStateId,
    configData?.openStateId,
    configData?.closeStateId,
    configData,
    handleConfigDataChange,
    linearWorkflowStates,
  ]);

  const handleProjectKeyChange = (projectKey: string) => {
    handleConfigDataChange({
      ...configData,
      projectKey,
      issueType: '', // Reset issue type when project changes
    });
  };

  const getInputsConfiguration = useMemo(() => {
    // `isDepSatisfied` (module scope) is the single source of truth for the
    // dependsOn rule, shared with `getMissingRequiredSelects` so input
    // rendering and submit-gating stay in lockstep.
    const mapInputConfiguration = (
      input: IntegrationInput & {
        dependsOn?: string;
        hideTillDependsOn?: boolean;
      }
    ) => {
      const {
        dependsOn,
        hideTillDependsOn,
        hidden: hiddenInput,
        ...rest
      } = input;
      const options = getOptions(input, rest.name) || [];
      const depValue = dependsOn ? configData[dependsOn] : undefined;
      const depSatisfied = dependsOn ? isDepSatisfied(depValue) : true;
      const hidden = hideTillDependsOn ? !depSatisfied : hiddenInput;
      return {
        ...rest,
        disabled: dependsOn
          ? !depSatisfied
          : rest.type === Input.Type.SELECT
            ? options.length === 0
            : false,
        hidden,
        options,
        error: formErrors[input.name] ? ' ' : undefined,
      };
    };

    // Filter out projectKey for Jira - it will be rendered separately
    const inputs = (integration?.configuration?.inputs || []).filter(
      input => !(isTicketingIntegration && input.name === 'projectKey')
    );

    return (
      inputs.map(({ dependsOn, ...input }) =>
        mapInputConfiguration({ ...input, dependsOn })
      ) || []
    );
  }, [
    integration?.configuration?.inputs,
    configData,
    formErrors,
    getOptions,
    isTicketingIntegration,
  ]);

  // Determine if transitions tab should be shown
  const shouldShowTransitionsTab = Boolean(integration?.transitions);
  const availableTabs = shouldShowTransitionsTab
    ? configurationTabs
    : configurationTabs.filter(tab => tab.value !== 'transitions');

  return (
    <>
      {(step === 'configuration' ||
        step === 'transitions' ||
        step === 'permissions') &&
        integration.configuration?.permissions && (
          <Tabs
            tabs={availableTabs}
            selected={availableTabs.find(tab => tab.value === step) || null}
            onSelect={tab =>
              setStep(
                tab.value as 'configuration' | 'transitions' | 'permissions'
              )
            }
          />
        )}

      <Container
        className={cn(
          'mt-4 hidden space-y-4',
          step === 'configuration' && 'block'
        )}
      >
        {isTicketingIntegration && (
          <div className="space-y-1">
            <label
              htmlFor="projectKey"
              className="text-chariot-text-primary block text-sm font-medium"
            >
              Project
            </label>
            <JiraProjectSearch
              value={configData.projectKey as string}
              onChange={handleProjectKeyChange}
              searchProjects={searchProjects}
              initialProjects={projects}
              error={formErrors.projectKey ? ' ' : undefined}
              placeholder={
                integration?.projectSearchPlaceholder ??
                'Search for a project...'
              }
            />
          </div>
        )}
        <Inputs
          inputs={getInputsConfiguration}
          onChange={handleConfigDataChangeWithLinearReset}
          values={configData}
          defaultValues={defaultConfigValues}
        />
        {/* Required custom-field defaults — Jira only. Mounted directly under
            the severities/autoCreate inputs so the UX framing is "what gets
            pre-populated on every auto-created ticket above threshold". */}
        {integration?.id === 'jira' &&
          isTicketingIntegration &&
          onCustomFieldDefaultsChange && (
            <JiraCustomFieldDefaults
              accountKey={accountKey}
              projectKey={configData.projectKey as string}
              issueType={configData.issueType as string}
              config={
                accountKey ? undefined : convertObjectValuesToStrings(setupData)
              }
              value={customFieldDefaults ?? []}
              onChange={onCustomFieldDefaultsChange}
            />
          )}
        {integration?.id === 'jira' &&
          isTicketingIntegration &&
          onPriorityConfigChange && (
            <JiraPriorityMapping
              accountKey={accountKey}
              config={
                accountKey ? undefined : convertObjectValuesToStrings(setupData)
              }
              value={priorityConfig}
              onChange={onPriorityConfigChange}
            />
          )}
        {integration?.id === 'jira' && isTicketingIntegration && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Checkbox
                id="auto-retest-on-resolution"
                checked={configData.autoRetestOnResolution === 'true'}
                onCheckedChange={(checked: boolean) => {
                  handleConfigDataChange({
                    ...configData,
                    autoRetestOnResolution: checked ? 'true' : 'false',
                  });
                  if (!checked) {
                    onRetestTransitionsChange?.([]);
                  }
                }}
              />
              <Label
                htmlFor="auto-retest-on-resolution"
                className="text-chariot-text-primary cursor-pointer text-sm font-medium"
              >
                Automatically retest on ticket transition
              </Label>
              <Tooltip
                placement="right"
                title="When enabled, Guard will automatically re-run the scan that found a risk when its Jira ticket is transitioned to one of the selected statuses."
                tooltipClassName="max-w-xs"
              >
                <ExclamationCircleIcon className="size-4" aria-hidden="true" />
              </Tooltip>
            </div>
            {configData.autoRetestOnResolution === 'true' && (
              <div className="ml-6 space-y-2">
                <p className="text-chariot-text-secondary text-xs">
                  Select which ticket statuses should trigger a rescan:
                </p>
                {availableStatuses.length === 0 ? (
                  <p className="text-chariot-text-secondary text-xs italic">
                    {configData.projectKey
                      ? 'Loading statuses...'
                      : 'Select a project to see available statuses.'}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {availableStatuses.map(status => {
                      const statusName = status.to?.name ?? status.name;
                      const isSelected = retestTransitions?.some(
                        t => t.name === statusName
                      );
                      return (
                        <div
                          key={status.id}
                          className="flex items-center gap-2"
                        >
                          <Checkbox
                            id={`retest-status-${status.id}`}
                            checked={isSelected}
                            onCheckedChange={(checked: boolean) => {
                              const current = retestTransitions ?? [];
                              const next = checked
                                ? [
                                    ...current,
                                    { id: status.id, name: statusName },
                                  ]
                                : current.filter(t => t.name !== statusName);
                              onRetestTransitionsChange?.(next);
                            }}
                          />
                          <Label
                            htmlFor={`retest-status-${status.id}`}
                            className="text-chariot-text-primary cursor-pointer text-sm"
                          >
                            {statusName}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {integration?.id === 'jira' && isTicketingIntegration && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-chariot-text-primary text-sm font-medium">
                Additional resolved statuses
              </Label>
              <Tooltip
                placement="right"
                title="Tickets in these statuses will be treated as resolved. Use this for post-closure states (e.g. 'Accepted') that should not reopen the risk in Guard."
                tooltipClassName="max-w-xs"
              >
                <ExclamationCircleIcon className="size-4" aria-hidden="true" />
              </Tooltip>
            </div>
            {availableStatuses.length === 0 ? (
              <p className="text-chariot-text-secondary text-xs italic">
                {configData.projectKey
                  ? 'Loading statuses...'
                  : 'Select a project to see available statuses.'}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {availableStatuses.map(status => {
                  const statusName = status.to?.name ?? status.name;
                  const isSelected =
                    additionalResolvedStatuses?.some(
                      t => t.name === statusName
                    ) ?? false;
                  return (
                    <div key={status.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`resolved-status-${status.id}`}
                        checked={isSelected}
                        onCheckedChange={(checked: boolean) => {
                          const current = additionalResolvedStatuses ?? [];
                          const next = checked
                            ? [...current, { id: status.id, name: statusName }]
                            : current.filter(t => t.name !== statusName);
                          onAdditionalResolvedStatusesChange?.(next);
                        }}
                      />
                      <Label
                        htmlFor={`resolved-status-${status.id}`}
                        className="text-chariot-text-primary cursor-pointer text-sm"
                      >
                        {statusName}
                      </Label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Container>

      {step === 'transitions' && integration?.transitions && (
        <Container className="mt-4">
          <TransitionsPanel
            accountKey={accountKey}
            projectKey={configData.projectKey as string}
            issueType={configData.issueType as string}
            config={
              accountKey ? undefined : convertObjectValuesToStrings(setupData)
            }
            value={transitionsData}
            onChange={onTransitionsChange}
            disabled={!configData.projectKey}
          />
        </Container>
      )}

      {step === 'permissions' && (
        <Container className="mt-4 space-y-4">
          {Object.values(integration.configuration?.permissions ?? {})?.map(
            ({ key, name, description, permission }) => {
              const missingPermissions =
                templatesError?.[configData.id]?.missingPermissions ?? [];
              const havePermission = !missingPermissions.includes(key);
              return (
                <PermissionRow
                  key={key}
                  name={name}
                  description={description}
                  permission={permission}
                  havePermission={havePermission}
                />
              );
            }
          )}
        </Container>
      )}
    </>
  );
};

export const PermissionRow = ({
  name,
  description,
  permission,
  havePermission,
}: {
  name: string;
  description: string;
  permission: string;
  havePermission: boolean;
}) => {
  return (
    <div className="[&:not(:last-child)]:border-chariot-stroke flex items-center justify-between gap-2 pb-4 not-last:border-b">
      <div>
        <p
          className={cn(
            'text-sm font-bold',
            !havePermission && 'text-chariot-stroke-critical'
          )}
        >
          {name}
        </p>
        <p className="text-chariot-text-secondary text-xs">{description}</p>
      </div>
      <BaseTag
        className={cn(
          'h-6 px-2 py-1',
          !havePermission && 'text-chariot-stroke-critical'
        )}
      >
        {permission}
      </BaseTag>
    </div>
  );
};

const OrgSelector = ({
  organizations,
  selectedIds,
  onChange,
}: {
  organizations: SetupOption[];
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
}) => {
  const allSelected = selectedIds.size === organizations.length;
  const toggleAll = () => {
    onChange(allSelected ? new Set() : new Set(organizations.map(o => o.id)));
  };
  const toggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(next);
  };
  return (
    <Container className="mt-4">
      <div className="mb-3 flex items-center justify-between">
        <p className="text-chariot-text-primary text-sm font-medium">
          Select Organizations
        </p>
        <button
          type="button"
          onClick={toggleAll}
          className="text-chariot-brand-primary text-xs hover:underline"
        >
          {allSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      <p className="text-chariot-text-secondary mb-3 text-xs">
        Choose which organizations to import data from. All organizations are
        selected by default.
      </p>
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {organizations.map(org => (
          <label
            key={org.id}
            className="hover:bg-chariot-surface-secondary flex cursor-pointer items-center gap-2 rounded px-2 py-1.5"
          >
            <input
              type="checkbox"
              checked={selectedIds.has(org.id)}
              onChange={() => toggle(org.id)}
              className="border-chariot-stroke text-chariot-brand-primary focus:ring-chariot-brand-primary h-4 w-4 rounded"
            />
            <span className="text-chariot-text-primary text-sm">
              {org.name}
            </span>
          </label>
        ))}
      </div>
    </Container>
  );
};

// Values that must never survive serialization into a saved template/account.
// Covers both JS `undefined`/`null` and the literal strings they stringify to.
const isUndefinedLike = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  value === 'undefined' ||
  value === 'null';

// Coerce an undefined-like scalar to '' so it never becomes the literal
// "undefined"/"null" once String()-ed downstream.
const emptyIfUndefinedLike = (value: unknown): string =>
  isUndefinedLike(value) ? '' : String(value);

// Replace every undefined-like field in a template object with '' so a stale
// `"undefined"` (or a JS undefined) can't be serialized back into settings.
const sanitizeTemplate = <T extends Record<string, unknown>>(obj: T): T =>
  Object.entries(obj).reduce(
    (acc, [key, value]) => {
      acc[key] = isUndefinedLike(value) ? '' : value;
      return acc;
    },
    {} as Record<string, unknown>
  ) as T;

// A dependency is "satisfied" when it holds a value: truthy (including a
// non-empty string like a chosen projectKey/teamId) but NOT the explicit
// checkbox-off string 'false'. This is the single source of truth for the
// dependsOn rule — `getInputsConfiguration` reuses it so input rendering and
// submit-gating agree on what counts as reachable.
const isDepSatisfied = (val: unknown): boolean =>
  Boolean(val) && val !== 'false';

// Whether an input is currently hidden / unreachable for the given form data.
// Mirrors the rendering rule: an input with `hideTillDependsOn` is hidden until
// its `dependsOn` dependency is satisfied; otherwise it honors its static
// `hidden` flag. Used to keep submit-gating in lockstep with what the user can
// actually see and fill (the `Input` component returns null for `hidden`).
const isInputHidden = (
  input: IntegrationInput & { hideTillDependsOn?: boolean; dependsOn?: string },
  data: Values
): boolean => {
  if (input.hideTillDependsOn) {
    return !isDepSatisfied(input.dependsOn ? data[input.dependsOn] : undefined);
  }
  return Boolean(input.hidden);
};

/**
 * Names of required SELECT inputs whose value is empty or the literal
 * "undefined"/"null" — i.e. the user hasn't made a real selection yet. Used to
 * gate the Confirm/submit button so a half-built routing config (the source of
 * persisted "undefined" project/team values) can't be saved.
 *
 * Only SELECT inputs are gated here; free-text required fields keep their
 * existing on-submit validation. Optional inputs (e.g. Linear's project) are
 * intentionally excluded.
 *
 * Crucially, only inputs that are CURRENTLY VISIBLE/reachable are gated. A
 * required select that is `hidden`, or whose `dependsOn` dependency is not yet
 * satisfied (e.g. Linear's `openStateId`/`closeStateId` before a team is
 * chosen), is excluded — otherwise the button would be dead-locked with no way
 * to satisfy a field the user can't see. Always-visible required selects (e.g.
 * Azure DevOps's `parentKey`) are gated as before.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure predicate exported alongside components for testability, mirrors useFormValidation above
export const getMissingRequiredSelects = (
  inputs: IntegrationMeta['inputs'] | undefined,
  data: Values
): string[] =>
  (inputs ?? [])
    .filter(input => input.required && input.type === Input.Type.SELECT)
    .filter(input => !isInputHidden(input, data))
    .filter(
      input => isUndefinedLike(data[input.name]) || data[input.name] === ''
    )
    .map(input => input.name);

const convertObjectValuesToStrings = (
  data: Record<string, string | number>
): ValidationCredentials =>
  Object.entries(data).reduce((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {} as ValidationCredentials);

export default SetupModal;
